import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Navigation, Lock, Fingerprint } from "lucide-react";
import API from "@/utils/api";
import { isBiometryAvailable, loginWithBiometry } from "@/utils/biometry";

export default function RiderLogin() {
    const navigate = useNavigate();
    const [phone, setPhone] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [biometrySupported, setBiometrySupported] = useState(false);

    useEffect(() => {
        isBiometryAvailable().then(setBiometrySupported);

        // Auto-sugerir si ya lo activó antes
        const wasEnabled = localStorage.getItem("rider_biometric_enabled") === "true";
        if (wasEnabled && !phone) {
            const lastPhone = JSON.parse(localStorage.getItem("rider_data") || "{}").phone;
            if (lastPhone) setPhone(lastPhone);
        }
    }, []);

    const saveSession = (data) => {
        localStorage.setItem("rider_token", data.access_token);
        localStorage.setItem("rider_data", JSON.stringify(data.driver));

        if (window.updateManifest) window.updateManifest();

        toast.success("Bienvenido a Conexo");
        navigate("/rider/dashboard");
    };

    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);

        try {
            const res = await axios.post(`${API}/rider/login`, { phone, password });
            saveSession(res.data);
        } catch (error) {
            if (error.response?.status === 401) {
                toast.error("Teléfono o contraseña incorrectos");
            } else {
                toast.error("Error de conexión con el servidor");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBiometricLogin = async () => {
        if (!phone) {
            toast.error("Ingresa tu teléfono primero");
            return;
        }

        setLoading(true);
        try {
            const res = await loginWithBiometry(phone);
            saveSession(res);
            toast.success("¡Identidad confirmada!");
        } catch (error) {
            console.error(error);
            toast.error("No se pudo verificar la huella o no está registrada");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black text-white font-sans flex flex-col justify-center items-center p-6 relative overflow-hidden sm:max-w-md sm:mx-auto">
            {/* Background effects */}
            <div className="absolute top-0 -left-1/4 w-full h-96 bg-primary/20 blur-[100px] pointer-events-none rounded-full" />
            <div className="absolute bottom-0 -right-1/4 w-full h-96 bg-primary/10 blur-[100px] pointer-events-none rounded-full" />

            {/* Branding container */}
            <div className="flex flex-col items-center mb-10 z-10 w-full animate-fade-in-up">
                <div className="w-20 h-20 bg-white text-primary rounded-3xl shadow-[0_0_40px_rgba(255,255,255,0.1)] flex items-center justify-center mb-6 border border-white/10 relative overflow-hidden">
                    <img src="/icons/icon-512x512.png" alt="Conexo Logo" className="w-full h-full object-cover p-2" />
                </div>
                <h1 className="text-4xl font-black tracking-tighter mb-2 italic text-white uppercase">CONEXO</h1>
                <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-2 opacity-60">
                    SaaS Platform • Delivery
                </p>
            </div>

            {/* Login form */}
            <div className="w-full max-w-sm z-10 bg-white/5 border border-white/10 rounded-3xl p-6 backdrop-blur-xl shadow-2xl animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-1">
                        <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 ml-1">Teléfono</label>
                        <div className="relative">
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                className="w-full bg-black/40 border border-white/10 text-white placeholder-slate-500 rounded-xl p-4 pl-4 text-sm font-bold outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all shadow-inner"
                                placeholder="Ingresa tu teléfono..."
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-[10px] uppercase font-black tracking-widest text-slate-400 ml-1">Contraseña</label>
                        <div className="relative">
                            <Lock size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-black/40 border border-white/10 text-white placeholder-slate-500 rounded-xl p-4 pr-10 text-sm font-bold outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all shadow-inner"
                                placeholder="••••••••"
                                required
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-wider text-sm py-4 rounded-xl shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all active:scale-[0.98] disabled:opacity-50 mt-4 flex items-center justify-center gap-2"
                    >
                        {loading ? "Verificando..." : "Ingresar"}
                        <Truck size={16} className={loading ? "animate-bounce" : ""} />
                    </button>

                    {biometrySupported && (
                        <div className="pt-4 border-t border-white/5 mt-4">
                            <button
                                type="button"
                                onClick={handleBiometricLogin}
                                disabled={loading}
                                className="w-full bg-white/5 hover:bg-white/10 text-white font-black uppercase tracking-wider text-[10px] py-4 rounded-xl border border-white/10 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                            >
                                <Fingerprint size={16} className="text-primary" />
                                Entrar con Huella / FaceID
                            </button>
                        </div>
                    )}
                </form>
            </div>

            <p className="mt-8 text-xs text-slate-600 font-black tracking-widest uppercase animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                Conexo POS Platform
            </p>
        </div>
    );
}
